import { DataMapDto } from "../../server/dto/datamap/DataMapDto";
import {
  DataMapDisclosureUpdateOperation,
  DataMapReceivedUpdateOperation,
  DataMapRecipientAssociationUpdateOperation,
  DataMapSourceAssociationUpdateOperation,
} from "../../server/dto/datamap/DataMapRequestDto";
import _ from "lodash";
import { UUIDString } from "../../server/Types";
import { useMemo } from "react";
import {
  DataMapReceivedFilter,
  DataMapDisclosedFilter,
  DataMapCollectedFilter,
} from "./dataMapTransformers";
import hash from "object-hash";

export const dataMapPendingCollectedPicIds = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
): UUIDString[] => {
  const ops = dataMap?._local?.operations ?? [];

  let pendingCollected = ops.filter(
    (op) => op.target == "COLLECTION",
  ) as DataMapReceivedUpdateOperation[];

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    pendingCollected = pendingCollected.filter((op) => filter?.includes(op.groupId));
  }

  if (filters.collectionGroupType) {
    const filter = [filters.collectionGroupType].flat();
    pendingCollected = pendingCollected.filter((op) =>
      filter.includes(dataMap.collectionGroups[op.groupId].type),
    );
  }

  if (filters.picGroupId) {
    const filter = [filters.picGroupId].flat();
    pendingCollected = pendingCollected.filter((op) =>
      filter.includes(dataMap.categories[op.picId]?.picGroupId),
    );
  }

  if (filters.surveyClassification) {
    const filter = [filters.picGroupId].flat();
    pendingCollected = pendingCollected.filter((op) =>
      filter.includes(dataMap.categories[op.picId]?.surveyClassification),
    );
  }

  if (filters.exclude?.collectionGroupType) {
    const excludeFilter = [filters.exclude.collectionGroupType].flat();
    pendingCollected = pendingCollected.filter(
      (op) => !excludeFilter.includes(dataMap.collectionGroups[op.groupId].type),
    );
  }

  return pendingCollected.map((op) => op.picId);
};

export const dataMapPendingDisclosedPicGroupIds = (
  dataMap: DataMapDto,
  filters: DataMapDisclosedFilter = {},
): UUIDString[] => {
  const ops = dataMap?._local?.operations ?? [];

  let pending = ops.filter((op) => op.target == "DISCLOSURE") as DataMapDisclosureUpdateOperation[];

  if (filters.collectionGroupType) {
    const filter = [filters.collectionGroupType].flat();
    pending = pending.filter((op) => filter.includes(dataMap.collectionGroups[op.groupId].type));
  }

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    pending = pending.filter((op) => filter.includes(op.groupId));
  }

  if (filters.vendorId) {
    const filter = [filters.vendorId].flat();
    pending = pending.filter((c) => filter.includes(c.vendorId));
  }

  if (filters.exclude?.collectionGroupType) {
    const excludeFilter = [filters.exclude.collectionGroupType].flat();
    pending = pending.filter(
      (op) => !excludeFilter.includes(dataMap.collectionGroups[op.groupId].type),
    );
  }

  return _.uniq(pending.map((op) => op.picGroupId));
};

export const dataMapPendingReceivedPicIds = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): UUIDString[] => {
  const ops = dataMap?._local?.operations ?? [];

  let pendingReceived = ops.filter(
    (op) => op.target == "RECEIVED",
  ) as DataMapReceivedUpdateOperation[];

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    pendingReceived = pendingReceived.filter((op) => filter.includes(op.groupId));
  }

  if (filters.vendorId) {
    const filter = [filters.vendorId].flat();
    pendingReceived = pendingReceived.filter((op) => filter.includes(op.vendorId));
  }

  return pendingReceived.map((op) => op.picId);
};

export const dataMapPendingRecipientAssocationCollectionGroupIds = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): UUIDString[] => {
  const ops = dataMap?._local?.operations ?? [];

  let pendingAssociations = ops.filter(
    (op) => op.target == "RECIPIENT_ASSOCIATION",
  ) as DataMapRecipientAssociationUpdateOperation[];

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    pendingAssociations = pendingAssociations.filter((op) => filter.includes(op.groupId));
  }

  if (filters.vendorId) {
    const filter = [filters.vendorId].flat();
    pendingAssociations = pendingAssociations.filter((op) => filter.includes(op.vendorId));
  }

  return pendingAssociations.map((op) => op.groupId);
};

export const dataMapPendingSourceAssocationCollectionGroupIds = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): UUIDString[] => {
  const ops = dataMap?._local?.operations ?? [];

  let pendingAssociations = ops.filter(
    (op) => op.target == "SOURCE_ASSOCIATION",
  ) as DataMapSourceAssociationUpdateOperation[];

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    pendingAssociations = pendingAssociations.filter((op) => filter.includes(op.groupId));
  }

  if (filters.vendorId) {
    const filter = [filters.vendorId].flat();
    pendingAssociations = pendingAssociations.filter((op) => filter.includes(op.vendorId));
  }

  return pendingAssociations.map((op) => op.groupId);
};

export const useDataMapPendingCollectedPicIds = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
): UUIDString[] => {
  return useMemo(() => dataMapPendingCollectedPicIds(dataMap, filters), [dataMap, hash(filters)]);
};

export const useDataMapPendingReceivedPicIds = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): UUIDString[] => {
  return useMemo(() => dataMapPendingReceivedPicIds(dataMap, filters), [dataMap, hash(filters)]);
};

export const useDataMapPendingDisclosedPicGroupIds = (
  dataMap: DataMapDto,
  filters: DataMapDisclosedFilter = {},
): UUIDString[] => {
  return useMemo(
    () => dataMapPendingDisclosedPicGroupIds(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

export const useDataMapPendingRecipientAssocationCollectionGroupIds = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): UUIDString[] => {
  return useMemo(
    () => dataMapPendingRecipientAssocationCollectionGroupIds(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

export const useDataMapPendingSourceAssocationCollectionGroupIds = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): UUIDString[] => {
  return useMemo(
    () => dataMapPendingSourceAssocationCollectionGroupIds(dataMap, filters),
    [dataMap, hash(filters)],
  );
};
